import React, { useState } from 'react';
import { X, Users, Bot, Search } from 'lucide-react';
import TeamChat from './TeamChat';
import AIChat from './AIChat';
import ChatSidebar from './ChatSidebar';
import NewThreadWizard from './NewThreadWizard';
import { useRAGChat } from '../hooks/useRAGChat';

const getThreadId = (thread) => thread?.thread_id || thread?.id || thread?._id;

const getThreadTitle = (thread, loggedInUserId) => {
    if (!thread) return 'Untitled thread';
    if (thread.chat_category === 'personal' || thread.is_group === false) {
        if (thread.participants && Array.isArray(thread.participants)) {
            const other = thread.participants.find(p => String(p.user_id) !== String(loggedInUserId));
            if (other?.display_name) return other.display_name.replace(/raci/i, 'RACI');
            if (thread.participants.length === 1 && String(thread.participants[0].user_id) === String(loggedInUserId))
                return ((thread.participants[0].display_name || 'You') + ' (You)').replace(/raci/i, 'RACI');
        }
    }
    const raw = thread.thread_name || thread.display_name || thread.title || thread.name ||
        (thread.scope_type === 'RFP_GENERAL' ? 'Everyone on this proposal' : 'Conversation');
    return raw.replace(/^Section\s*\(/i, '').replace(/\)$/, '').trim().replace(/raci/i, 'RACI');
};

const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
};

const getAvatarColor = (name) => {
    if (!name) return '#64748B';
    const colors = ['#FF5526', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#6366F1', '#14B8A6', '#F97316'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

const formatRole = (role) => {
    if (!role) return '';
    return role
        .split(/[_-]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

/* ─── Members Drawer ──────────────────────────────────────────────────── */
const MembersDrawer = ({ participants = [], currentUserId, onClose }) => (
    <div className="vch-members-drawer-overlay" onClick={onClose}>
        <div className="vch-members-drawer" onClick={e => e.stopPropagation()}>
            <div className="vch-members-drawer-header">
                <span>Team Members</span>
                <button className="vch-icon-btn" onClick={onClose}><X size={16} /></button>
            </div>
            <div className="vch-members-drawer-list">
                {participants.map((p, i) => {
                    const name = p.display_name || 'Unknown';
                    const initials = getInitials(name);
                    const bg = getAvatarColor(name);
                    const isOnline = p.is_online || p.status === 'online';
                    const isSelf = String(p.user_id) === String(currentUserId);
                    return (
                        <div key={p.user_id || i} className="vch-member-row">
                            <div className="vch-member-avatar-wrap">
                                <div className="vch-member-avatar" style={{ background: bg }}>
                                    {initials}
                                </div>
                            </div>
                            <div className="vch-member-info">
                                <span className="vch-member-name">{name}{isSelf ? ' (You)' : ''}</span>
                                {p.role && <span className="vch-member-role">{formatRole(p.role)}</span>}
                            </div>
                        </div>
                    );
                })}
                {participants.length === 0 && (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#94A3B8', fontSize: '13px' }}>
                        No participants found.
                    </div>
                )}
            </div>
        </div>
    </div>
);

/* ─── Chat Modal ──────────────────────────────────────────────────────── */
const ChatModal = ({ onClose, chatController, rfpId, proposalTitle, activeTab: propActiveTab, setActiveTab: propSetActiveTab }) => {
    const [localActiveTab, setLocalActiveTab] = useState('ai');
    const activeTab = propActiveTab !== undefined ? propActiveTab : localActiveTab;
    const setActiveTab = propSetActiveTab !== undefined ? propSetActiveTab : setLocalActiveTab;

    const [showWizard, setShowWizard] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [showWarning, setShowWarning] = useState(false);
    const [showMembers, setShowMembers] = useState(false);
    const [aiInput, setAiInput] = useState('');
    const [isRenaming, setIsRenaming] = useState(false);
    const [renameTitle, setRenameTitle] = useState('');

    const ragChat = useRAGChat({ rfpId, isAITabActive: activeTab === 'ai' });

    React.useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    const handleBackdropClick = (e) => {
        if (e.target.classList.contains('v-ch-full-backdrop')) {
            setShowWarning(true);
            setTimeout(() => setShowWarning(false), 2500);
        }
    };

    const handleAISend = () => {
        if (!aiInput.trim()) return;
        ragChat.sendMessage(aiInput, ragChat.selectedSessionId);
        setAiInput('');
    };

    const selectedThread = React.useMemo(() => {
        if (!chatController?.threads || !chatController?.selectedThreadId) return null;
        return chatController.threads.find(t => String(getThreadId(t)) === String(chatController.selectedThreadId));
    }, [chatController?.selectedThreadId, chatController?.threads]);

    React.useEffect(() => {
        if (selectedThread) setRenameTitle(getThreadTitle(selectedThread, chatController.currentUserId));
        setIsRenaming(false);
    }, [chatController?.selectedThreadId, selectedThread, chatController?.currentUserId]);

    const handleRenameSubmit = async () => {
        if (!renameTitle.trim() || !chatController.renameThread || !chatController.selectedThreadId) return;
        const ok = await chatController.renameThread(chatController.selectedThreadId, renameTitle.trim());
        if (ok) setIsRenaming(false);
    };

    // Participants & online stats
    const participants = selectedThread?.participants || [];
    const participantCount = selectedThread?.participant_count || participants.length || 0;
    const onlineCount = participants.filter(p => p.is_online || p.status === 'online').length;
    const threadTitle = activeTab === 'ai' ? 'Velocity AI Assistant' :
        (chatController.loadingThreads ? 'Loading...' :
            (selectedThread ? getThreadTitle(selectedThread, chatController.currentUserId) :
                (proposalTitle || 'Team Discussion')));

    const isGroup = selectedThread ? (
        String(selectedThread.chat_category || '').toLowerCase() === 'group' ||
        selectedThread.is_group === true ||
        selectedThread.scope_type === 'RFP_GENERAL' ||
        selectedThread.scope_type === 'RFP_SECTION'
    ) : false;

    const threadAvatarBg = getAvatarColor(threadTitle);
    const threadInitials = getInitials(threadTitle);

    return (
        <div className="v-ch-full-backdrop" onClick={handleBackdropClick}>
            {showWarning && (
                <div className="v-ch-click-warning">Please close the chat to interact with the RFP</div>
            )}

            <div className="v-ch-modal-container" onClick={e => e.stopPropagation()}>
                <div className={`v-ch-modal ${isSidebarOpen ? '' : 'sidebar-collapsed'}`} style={{ overflow: 'hidden', position: 'relative' }}>

                    {/* ── LEFT SIDEBAR ── */}
                    <ChatSidebar
                        isOpen={isSidebarOpen}
                        onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
                        activeTab={activeTab}
                        threads={chatController.threads}
                        selectedThreadId={chatController.selectedThreadId}
                        onSelectThread={chatController.openThread}
                        onOpenWizard={() => setShowWizard(true)}
                        hasRfpContext={chatController.hasRfpContext}
                        currentUserId={chatController.currentUserId}
                        aiSessions={ragChat.sessions}
                        selectedSessionId={ragChat.selectedSessionId}
                        onSelectSession={ragChat.selectSession}
                        onDeleteSession={ragChat.deleteSession}
                        onRenameSession={ragChat.renameSession}
                        onCreateSession={ragChat.startNewSession}
                        loadingSessions={ragChat.loadingSessions}
                        sessionsError={ragChat.sessionsError}
                        loadingThreads={chatController.loadingThreads}
                    />

                    {/* ── RIGHT MAIN AREA ── */}
                    <div className="vch-main-area" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', minWidth: 0 }}>
                        {/* Header */}
                        <header className="vch-header">
                            <div className="vch-header-top">
                                {/* Left: Thread info */}
                                <div className="vch-header-left">
                                    {/* Thread title */}
                                    <div className="vch-header-thread-info">
                                        <div className="vch-header-title-wrap">
                                            <div
                                                className={`vch-header-title ${activeTab === 'team' && selectedThread && chatController.renameThread ? 'editable' : ''}`}
                                                onClick={() => {
                                                    if (activeTab === 'team' && selectedThread && chatController.renameThread) {
                                                        setRenameTitle(getThreadTitle(selectedThread, chatController.currentUserId));
                                                        setIsRenaming(true);
                                                    }
                                                }}
                                                title={threadTitle}
                                            >
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{threadTitle}</span>
                                                {activeTab === 'team' && selectedThread && chatController.renameThread && (
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="vch-edit-icon" style={{ marginLeft: '6px', opacity: 0.5, flexShrink: 0 }}>
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                        <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z" />
                                                    </svg>
                                                )}
                                            </div>
                                            {activeTab === 'team' && selectedThread && participantCount > 0 && (
                                                <div className="vch-header-subtitle">
                                                    {participantCount} Member{participantCount !== 1 ? 's' : ''}
                                                    {onlineCount > 0 && (
                                                        <><span className="vch-header-bullet">•</span>
                                                            <span className="vch-header-online">{onlineCount} Online</span>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                            {activeTab === 'ai' && (
                                                <div className="vch-header-subtitle">
                                                    <span className={`vch-pulse-dot ${chatController.connectionStatus === 'connected' ? 'green' : chatController.connectionStatus === 'connecting' ? 'amber' : 'gray'}`} />
                                                    {chatController.connectionStatus === 'connected' ? 'Live' :
                                                        chatController.connectionStatus === 'connecting' ? 'Connecting...' : 'Offline'}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Right: Actions */}
                                <div className="vch-header-right">
                                    {activeTab === 'team' && selectedThread && (
                                        <button
                                            className="vch-icon-btn vch-view-members-btn"
                                            onClick={() => setShowMembers(true)}
                                            title="View team members"
                                        >
                                            <Users size={14} />
                                            <span>View team members</span>
                                        </button>
                                    )}
                                    <button className="vch-icon-btn" onClick={onClose} title="Close">
                                        <X size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="vch-header-bottom">
                                {/* Tab switcher spanning full width */}
                                <div className="vch-tabs vch-tabs-full">
                                    <button
                                        className={`vch-tab ${activeTab === 'ai' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('ai')}
                                    >
                                        <Bot size={12} /> AI Assistant
                                    </button>
                                    <button
                                        className={`vch-tab ${activeTab === 'team' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('team')}
                                    >
                                        <Users size={12} /> Team Chat
                                    </button>
                                </div>
                            </div>
                        </header>

                        {/* Body */}
                        <div className="v-ch-body">
                            {activeTab === 'team' ? (
                                <TeamChat
                                    hasRfpContext={chatController.hasRfpContext}
                                    rfpId={rfpId}
                                    proposalTitle={proposalTitle}
                                    threads={chatController.threads}
                                    selectedThreadId={chatController.selectedThreadId}
                                    messages={chatController.messages}
                                    loadingThreads={chatController.loadingThreads}
                                    loadingMessages={chatController.loadingMessages}
                                    sendingMessage={chatController.sendingMessage}
                                    error={chatController.error}
                                    currentUserId={chatController.currentUserId}
                                    onSelectThread={chatController.openThread}
                                    onSendMessage={chatController.sendMessage}
                                    onOpenWizard={() => setShowWizard(true)}
                                    typingUsers={chatController.typingUsers}
                                    onEditMessage={chatController.editMessage}
                                    onDeleteMessage={chatController.deleteMessage}
                                    onRenameThread={chatController.renameThread}
                                    connectionStatus={chatController.connectionStatus}
                                    readWatermarks={chatController.readWatermarks}
                                />
                            ) : (
                                <AIChat
                                    rfpId={rfpId}
                                    messages={ragChat.messages}
                                    loadingMessages={ragChat.loadingMessages}
                                    messagesError={ragChat.messagesError}
                                    sendingMessage={ragChat.sendingMessage}
                                    input={aiInput}
                                    onInputChange={setAiInput}
                                    onSendMessage={handleAISend}
                                    activeSessionCreatedAt={
                                        ragChat.sessions.find(s => {
                                            const sid = s?.session_id ?? s?.id ?? s?._id;
                                            return String(sid) === String(ragChat.selectedSessionId);
                                        })?.created_at
                                    }
                                    connectionStatus={chatController.connectionStatus}
                                />
                            )}
                        </div>
                    </div>

                    {/* ── New Thread Wizard ── */}
                    <NewThreadWizard
                        isOpen={showWizard}
                        rfpId={rfpId}
                        currentUserId={chatController.currentUserId}
                        onClose={() => setShowWizard(false)}
                        onResolveThread={chatController.resolveThread}
                    />

                    {/* ── Rename Modal ── */}
                    {isRenaming && (
                        <div className="vch-modal-overlay" onClick={() => setIsRenaming(false)}>
                            <div className="vch-rename-modal" onClick={e => e.stopPropagation()}>
                                <h3 className="vch-rename-title">Rename Conversation</h3>
                                <p className="vch-rename-sub">Enter a new title for this discussion.</p>
                                <input
                                    type="text"
                                    value={renameTitle}
                                    onChange={e => setRenameTitle(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleRenameSubmit()}
                                    className="vch-rename-input"
                                    autoFocus
                                />
                                <div className="vch-rename-actions">
                                    <button className="vch-rename-cancel" onClick={() => setIsRenaming(false)}>Cancel</button>
                                    <button className="vch-rename-save" onClick={handleRenameSubmit}>Save Changes</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Members Drawer ── */}
                    {showMembers && (
                        <MembersDrawer
                            participants={participants}
                            currentUserId={chatController.currentUserId}
                            onClose={() => setShowMembers(false)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatModal;
